import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WealthReaderWidget, type WealthReaderConfig } from "../components/WealthReaderWidget";
import { fetchPayloadByOperation, type PayloadDetailResponse } from "../api";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;
/**
 * Días de histórico que se piden al widget en el alta de un banco.
 * Sin `date_from` el widget aplica su propio default (~2 días).
 * 89 = el máximo habitual antes de que muchos bancos disparen 2FA extra.
 */
const WIDGET_DATE_FROM_DAYS = 89;

type WaitState =
  | { kind: "idle" }
  | { kind: "polling"; sinceMs: number }
  | { kind: "received"; payload: PayloadDetailResponse }
  | { kind: "timeout" }
  | { kind: "error"; message: string };

/**
 * Pantalla "Conectar banco": muestra el selector de entidades de Wealth Reader.
 *
 * Flujo:
 *   1. Se genera un operation_id único por visita.
 *   2. El usuario completa el flujo en el iframe del widget.
 *   3. El widget emite "flow completed" → arranca el polling.
 *   4. Polling a GET /api/payloads/by-operation/:opId hasta encontrarlo o timeout.
 */
function isPayloadEmpty(body: unknown): boolean {
  const b = body as { payload?: { accounts?: unknown[]; loans?: unknown[]; user_information?: { ID?: unknown; name?: unknown } } } | null;
  const p = b?.payload;
  if (!p) return true;
  const accountsLen = Array.isArray(p.accounts) ? p.accounts.length : 0;
  const loansLen = Array.isArray(p.loans) ? p.loans.length : 0;
  const userId = typeof p.user_information?.ID === "string" ? p.user_information.ID : "";
  const userName = typeof p.user_information?.name === "string" ? p.user_information.name : "";
  return accountsLen === 0 && loansLen === 0 && !userId && !userName;
}

function dateFromDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - Math.max(1, Math.round(days)));
  return d.toISOString().slice(0, 10);
}

export function ConnectBank() {
  const operationId = useMemo(() => crypto.randomUUID(), []);
  const [wait, setWait] = useState<WaitState>({ kind: "idle" });
  const pollerRef = useRef<number | null>(null);

  const config: WealthReaderConfig = useMemo(
    () => ({
      operation_id: operationId,
      entities_to_display: [],
      wait_full_response: true,
      date_from: dateFromDaysAgo(WIDGET_DATE_FROM_DAYS),
      product_types: "accounts,loans",
      psd2: true,
      nonpsd2: true,
    }),
    [operationId],
  );

  const stopPolling = useCallback(() => {
    if (pollerRef.current !== null) {
      window.clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    const startedAt = Date.now();
    setWait({ kind: "polling", sinceMs: 0 });

    pollerRef.current = window.setInterval(async () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed > POLL_TIMEOUT_MS) {
        stopPolling();
        setWait({ kind: "timeout" });
        return;
      }
      try {
        const found = await fetchPayloadByOperation(operationId);
        if (found) {
          stopPolling();
          setWait({ kind: "received", payload: found });
        } else {
          setWait({ kind: "polling", sinceMs: elapsed });
        }
      } catch (err) {
        stopPolling();
        setWait({ kind: "error", message: err instanceof Error ? err.message : String(err) });
      }
    }, POLL_INTERVAL_MS);
  }, [operationId, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  return (
    <section className="card">
      <h2>Conectar banco</h2>
      <p className="meta">
        Selecciona una entidad para iniciar el flujo de consentimiento.
        Cuando termines, los datos llegarán al backend automáticamente.
      </p>
      <p className="meta">
        <strong>operation_id:</strong> <code>{operationId}</code>
        {" · "}
        <strong>date_from:</strong> <code>{config.date_from}</code>{" "}
        ({WIDGET_DATE_FROM_DAYS} días de histórico)
      </p>

      <WaitBanner wait={wait} />

      <WealthReaderWidget config={config} onComplete={startPolling} height={700} />
    </section>
  );
}

function WaitBanner({ wait }: { wait: WaitState }) {
  if (wait.kind === "idle") return null;

  if (wait.kind === "polling") {
    const seconds = Math.floor(wait.sinceMs / 1000);
    return (
      <div className="info">
        ⏳ Flujo completado. Esperando que el backend reciba el callback… ({seconds}s)
      </div>
    );
  }

  if (wait.kind === "received") {
    const { meta, body } = wait.payload;
    // Detectar payload OK pero sin contenido (usuario cerró el widget a
    // mitad de la selección de contrato en Bankinter multicontrato, etc.).
    const empty = isPayloadEmpty(body);
    if (meta.success && empty) {
      return (
        <div className="warning">
          ⚠️ <strong>Payload recibido pero sin datos.</strong> El widget devolvió
          success=true pero no trajo cuentas ni préstamos ni titular. Suele pasar
          cuando se cierra el widget antes de la pantalla de selección de contrato.
          Vuelve a pulsar "Conectar banco" y esta vez elige un contrato antes de cerrar.
        </div>
      );
    }
    return (
      <div className={meta.success ? "success" : "warning"}>
        {meta.success ? "✅" : "⚠️"} <strong>Payload recibido.</strong>{" "}
        success={String(meta.success)}{meta.errorCode !== 0 ? `, code=${meta.errorCode}` : ""}.
        Guardado como <code>{meta.filename}</code> ({(meta.sizeBytes / 1024).toFixed(1)} KB).
      </div>
    );
  }

  if (wait.kind === "timeout") {
    return (
      <div className="warning">
        ⌛ El callback no llegó al backend en 60 s. Revisa la configuración del webhook
        en el panel de Wealth Reader y los logs del backend.
      </div>
    );
  }

  return <div className="error">❌ Error al consultar el backend: {wait.message}</div>;
}
